import {type FetchResult, ServerError} from '@yandex-delivery/core/utils/network'
import type {TypedRequestConfig} from '@yandex-delivery/core/utils/network/types'

const UNAUTHORIZED_STATUS = 401
const FORBIDDEN_STATUS = 403
const NOT_FOUND_STATUS = 404
const AUTH_PATH_SEGMENT = '/v1/auth'

type AuthFetchConfig = TypedRequestConfig & {
  idempotent?: boolean
  retries?: number
}

type AuthFetch = (config: AuthFetchConfig) => Promise<FetchResult<unknown>>

const isSharedRouteUrl = (url?: string) => Boolean(url?.includes('/shared-route') || url?.includes('/web/state'))

const isUnauthorizedError = (error: ServerError) =>
  (error.status === UNAUTHORIZED_STATUS || error.status === FORBIDDEN_STATUS) && isSharedRouteUrl(error.url)

const isNotFoundError = (error: ServerError) => error.status === NOT_FOUND_STATUS && isSharedRouteUrl(error.url)

/**
 * Strips the /v1/auth segment from the current pathname to get
 * the base path of the non-auth sharing page.
 *
 * Example: /route/v1/auth/ → /route/
 */
const getNonAuthBasePath = (): string => {
  const currentPath = window.location.pathname
  const authIndex = currentPath.indexOf(AUTH_PATH_SEGMENT)
  const basePath = authIndex !== -1 ? currentPath.substring(0, authIndex) : currentPath.replace(/\/$/, '')

  return basePath.endsWith('/') ? basePath : `${basePath}/`
}

const redirectToDefaultSharedRoute = (error: ServerError) => {
  if (typeof window === 'undefined') {
    throw error
  }
  const sharingKey = window.location.hash.substring(1)
  if (sharingKey) {
    const basePath = getNonAuthBasePath()
    const target = `${basePath}#${sharingKey}`

    console.error(
      '[withNoAuthSharedRouteRedirect] Redirecting to default shared route',
      {sharingKey, target, url: error.url, status: error.status},
      error,
    )

    window.location.href = target
  }
}

const onCatch = (error: unknown) => {
  if (error instanceof ServerError) {
    if (isUnauthorizedError(error)) {
      console.warn(
        `[withNoAuthSharedRouteRedirect] Auth error ${error.status} from shared route, redirect to default shared route`,
      )
      return redirectToDefaultSharedRoute(error) as never
    }
    if (isNotFoundError(error)) {
      console.warn('[withNoAuthSharedRouteRedirect] 404 from auth shared route, falling back to default shared route')
      return redirectToDefaultSharedRoute(error) as never
    }
  }
  throw error
}

export const withNoAuthSharedRouteRedirect = <TFetch extends AuthFetch>(fetchFn: TFetch): TFetch =>
  ((config: AuthFetchConfig) => fetchFn(config).catch(onCatch)) as TFetch
